import { Add, Close } from '@mui/icons-material';
import { Box, Button, Fade, IconButton, InputAdornment, Typography, useTheme } from '@mui/material';
import { Control, Controller, FieldErrors, useFieldArray, UseFormWatch } from 'react-hook-form';
import { Renter } from '../../../utils/types/renter.interface';
import { RenterInviteTextInput } from '../components';

interface Props {
  control: Control<Renter, any>;
  errors: FieldErrors<Renter>;
  watch: UseFormWatch<Renter>;
}

export const RenterContactV2 = ({ control, errors, watch }: Props) => {
  const theme = useTheme();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'applicantEmails',
  });

  const applicantEmails = watch('applicantEmails');

  const errorMsgForApplicantEmail = (index: number) => {
    const errorMsg = errors?.applicantEmails?.[index]?.value?.message;
    if (errorMsg) return errorMsg;
    const value = applicantEmails?.[index]?.value?.toLowerCase();
    if (!value) return '';
    const isDuplicate =
      (applicantEmails?.filter((email) => email.value?.toLowerCase() === value).length ?? 0) > 1;
    return isDuplicate ? 'Some renters seem to share the same email. Please check again.' : '';
  };

  return (
    <>
      <Typography variant="h6" component="h2" fontWeight="bold !important" lineHeight="28px" mb={1}>
        Renter contact information
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontSize: '16px',
          [theme.breakpoints.down('md')]: {
            fontSize: '14px',
            lineHeight: '22px',
          },
        }}
        fontWeight="350"
        mb={2}
        letterSpacing={0}
      >
        Enter your renter's email address below. You can add up to five roommates, if applicable.
      </Typography>
      <Controller
        name="applicantEmails.0.value"
        control={control}
        render={({ field }) => (
          <RenterInviteTextInput
            {...field}
            ref={null}
            size="small"
            id="client-email-address"
            label="Renter Email Address"
            required
            autoComplete="section-renter email"
            error={!!errorMsgForApplicantEmail(0)}
            helperText={errorMsgForApplicantEmail(0)}
            sx={{ mt: 0 }}
            slotProps={{
              input: {
                classes: {
                  root: 'fs-mask',
                },
              },
            }}
          />
        )}
      />
      {fields.map(
        (roommate: { id: string }, index: number) =>
          index > 0 && (
            <Fade in={index > 0} key={roommate.id}>
              <Box sx={{ mt: 2 }}>
                <Controller
                  name={`applicantEmails.${index}.value`}
                  key={roommate.id}
                  control={control}
                  render={({ field }) => (
                    <RenterInviteTextInput
                      {...field}
                      ref={null}
                      size="small"
                      id={`applicant-emails-${index}`}
                      label={`Roommate ${index} email address`}
                      required
                      autoComplete={`section-roommate-${index} email`}
                      error={!!errorMsgForApplicantEmail(index)}
                      helperText={errorMsgForApplicantEmail(index)}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="remove roommate"
                                onClick={() => remove(index)}
                                edge="end"
                              >
                                <Close />
                              </IconButton>
                            </InputAdornment>
                          ),
                          disableUnderline: true,
                          classes: {
                            root: 'fs-mask',
                          },
                        },
                      }}
                    />
                  )}
                />
              </Box>
            </Fade>
          ),
      )}
      {fields.length <= 5 && (
        <Button
          variant="outlined"
          onClick={() =>
            append(
              { value: '' },
              { shouldFocus: true, focusName: `applicantEmails.${fields.length}.value` },
            )
          }
          id="add-roommate-button"
          sx={{
            mt: 2,
            padding: '12px',
            pl: 2,
            pr: 3,
            gap: 1,
            borderColor: `${theme.palette.neutralGrey?.[200] ?? theme.palette.grey[200]} !important`,
            [theme.breakpoints.down('md')]: {
              width: 'fit-content',
            },
          }}
        >
          <Add />
          Add roommates
        </Button>
      )}
    </>
  );
};
